import React, { FC } from 'react';
import { PayableReason } from '@scale/shared/remotasks/balanceSheet';
import { Icon, Tooltip } from '@scale/scaleui';
import { RewardType } from '@scale/shared/defaults';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { makeStyles } from 'design-system/styles';
import cn from 'classnames';
import ToolTipText from 'react-scale/components/Expert/Earnings/components/TooltipText';

interface DescriptionCellProps {
  submissionId?: string;
  reason: PayableReason;
  isQbp: boolean;
  isOvertime: boolean;
  rewardType?: RewardType;
  rewardTypeDetail?: string;
  details?: string;
}

interface TooltipDescriptionProps {
  submissionId?: string;
  tooltipText?: string;
  details?: string;
  cursor?: string;
  text?: string;
  isQbp: boolean;
  isOvertime: boolean;
}

const useStyles = makeStyles({
  qbpIcon: {
    color: '#01579B',
    fontSize: '12px',
  },
  qbpContainer: {
    background: '#E1F5FE',
    padding: '2px 8px',
    borderRadius: 36,
  },
});

export const QualityPayPill: React.FC<{}> = () => {
  const classes = useStyles();

  return (
    <span
      className={cn(classes.qbpContainer, 'flex', 'items-center', 'gap-2', 'font-medium', 'ml-2')}
    >
      <FontAwesomeIcon className={classes.qbpIcon} icon={['fas', 'stars']} />
      <span className={cn(classes.qbpIcon, 'whitespace-nowrap')}>Quality Pay</span>
    </span>
  );
};

const TooltipDescription = ({
  submissionId,
  tooltipText,
  details,
  cursor,
  text = '',
  isQbp,
  isOvertime,
}: TooltipDescriptionProps) => {
  let formattedText = details ? (tooltipText ? `${tooltipText}: ${details}` : details) : text;
  if (isOvertime) {
    formattedText = `2nd of two payments for task ID ${submissionId ?? '-'}`;
  }
  const helpOnHoverClass = cursor || 'cursor-help' || '';
  return (
    <span className="inline-flex items-center">
      {text || 'Other'}
      {isQbp && (
        <Tooltip
          delayDuration={100}
          side="top"
          text={
            <ToolTipText text="Incentive paid out for tasks accepted by reviewers" width={230} />
          }
          target={
            <span className={helpOnHoverClass}>
              <QualityPayPill />
            </span>
          }
        />
      )}
      {details && (
        <Tooltip
          contentClassName="max-w-[200px] whitespace-prewrap break-normal"
          delayDuration={100}
          side="top"
          text={<ToolTipText text={formattedText} width={230} />}
          target={
            <span>
              <Icon className={helpOnHoverClass} icon="info-circle" size="sm" variant="black" />
            </span>
          }
        ></Tooltip>
      )}
    </span>
  );
};

export const descriptionMap = {
  [PayableReason.Pay]: { tooltipText: 'Payment computed', text: 'Task Pay', cursor: 'cursor-help' },
  [PayableReason.Prepay]: {
    tooltipText: 'Early pay computed',
    text: 'Task Pay',
    cursor: 'cursor-help',
  },
  [PayableReason.PayAdjustment]: {
    tooltipText: 'Pay adjustment computed',
    text: 'Adjusted',
    cursor: 'cursor-help',
  },
  [PayableReason.ReferralReward]: {
    tooltipText: 'Referral Reward',
    text: 'Referral Reward',
    cursor: 'cursor-help',
  },
  [PayableReason.CourseReward]: { text: 'Training Reward' },
  [PayableReason.WorkLog]: { text: 'Work Log' },
  [PayableReason.HubstaffOperation]: { text: 'Hubstaff' },
  [PayableReason.BonusReward]: {
    tooltipText: 'Obtained Reward',
    text: 'Reward',
    cursor: 'cursor-help',
  },
  [PayableReason.ManualReward]: {
    tooltipText: 'Obtained Reward',
    text: 'Manual Reward',
    cursor: 'cursor-help',
  },
  [PayableReason.CrCOperation]: {
    text: 'CRC Operation',
  },
  [PayableReason.OvertimePay]: {
    tooltipText: 'Placeholder',
    text: 'Exceeded Time',
  },
  // Add other reasons here as needed
};

const PlatinumRewardDescription = () => (
  <Tooltip side="top" text={''} delayDuration={100} target={<span>Platinum Reward</span>}></Tooltip>
);

const StellarRewardDescription = () => (
  <Tooltip side="top" text={''} delayDuration={100} target={<span>Stellar Reward</span>}></Tooltip>
);

const MissionDescription = () => (
  <Tooltip side="top" text={''} delayDuration={100} target={<span>Mission Reward</span>}></Tooltip>
);

const DescriptionCell: FC<DescriptionCellProps> = ({
  submissionId,
  reason,
  isQbp,
  isOvertime,
  rewardType,
  rewardTypeDetail,
  details,
}) => {
  const config = descriptionMap[reason as keyof typeof descriptionMap] || {
    text: 'Other',
  };
  if (rewardType === RewardType.Mission) {
    return <MissionDescription />;
  }

  if (rewardTypeDetail) {
    if (/platinum/i.test(rewardTypeDetail)) {
      return <PlatinumRewardDescription />;
    } else if (/stellar/i.test(rewardTypeDetail)) {
      return <StellarRewardDescription />;
    }
  }

  return (
    <TooltipDescription
      submissionId={submissionId}
      tooltipText={(config as { tooltipText: string })?.tooltipText || ''}
      cursor={(config as { cursor: string })?.cursor || ''}
      details={details}
      text={config.text}
      isQbp={isQbp}
      isOvertime={isOvertime}
    />
  );
};

export default DescriptionCell;
